// Live pace on the setter's dial screen (REVIEW-2026-10-01 §4.4): dials this hour and today, talk time, connects, and
// time since the last dial. Pure; unit-tested in tests/fd-review-dialscreen.test.ts. Numbers come from GET /api/calls/pace.
import type { CallPace } from '../../../src/shared/api-dialer';

/** After this long without a dial (and no call up), "since last dial" turns amber. */
export const IDLE_AFTER_MS = 5 * 60_000;

/** 0:42, 4:05, 1 h 07 m. Negative or unknown spans read as 0:00. */
export function fmtSince(ms: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(ms) ? ms / 1000 : 0));
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const h = Math.floor(s / 3600);
  return `${h} h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')} m`;
}

/** Talk time: 0 m, 12 m, 1 h 05 m. */
export function fmtTalk(sec: number): string {
  const m = Math.max(0, Math.floor((Number.isFinite(sec) ? sec : 0) / 60));
  return m < 60 ? `${m} m` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`;
}

export interface PaceItem { key: string; label: string; value: string; title: string; warn?: boolean }

/**
 * The top-bar items. `lastDialAt` is the newer of the server's and a dial placed on this screen since the last refresh,
 * so "since last dial" restarts the moment the setter dials. `onCall` stops the idle warning while a call is up.
 */
export function paceItems(p: CallPace | null, now: number, opts: { localLastDialAt?: number | null; onCall?: boolean; booked?: number | null } = {}): PaceItem[] {
  if (!p) return [];
  const server = p.lastDialAt ? Date.parse(p.lastDialAt) : NaN;
  const local = opts.localLastDialAt ?? NaN;
  const last = Math.max(Number.isFinite(server) ? server : -Infinity, Number.isFinite(local) ? local : -Infinity);
  const since = Number.isFinite(last) ? now - last : null;
  const items: PaceItem[] = [
    { key: 'hour', label: 'Last hr', value: String(p.dialsLastHour), title: 'Dials in the last 60 minutes' },
    { key: 'today', label: 'Today', value: String(p.dialsToday), title: 'Dials today' },
    { key: 'connects', label: 'Connects', value: String(p.connectsToday), title: 'Calls a person answered today (gatekeeper or decision-maker)' },
    { key: 'talk', label: 'Talk', value: fmtTalk(p.talkSecToday), title: 'Talk time on those connects today' },
  ];
  if (typeof opts.booked === 'number') items.push({ key: 'booked', label: 'Booked', value: String(opts.booked), title: 'Meetings you booked today' });
  items.push({
    key: 'since', label: 'Since last dial', value: since === null ? '—' : fmtSince(since),
    title: since === null ? 'No dial yet today' : 'Time since you last pressed Dial',
    warn: since !== null && !opts.onCall && since >= IDLE_AFTER_MS,
  });
  return items;
}
